import React, { useEffect, useRef, useState } from 'react';
import Flex from '@deprecated-ui/common/Flex';
import Text from '@deprecated-ui/common/Text';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { selectors } from 'lib/entities/profile';
import PropTypes from 'prop-types';
import generateNavBar from 'utils/navbar';

import { clevertapEvent } from 'utils/clevertap';
import { CLEVERTAP_EVENT_NAME } from 'constants/clevertap';
import DATA_CY from 'constants/data-cy';
import Image from 'global/Image';

import IconStream from 'assets/customIcon/PopoverNavbarContainer/IconStream';
import IconProfile from 'assets/customIcon/PopoverNavbarContainer/IconProfile';
import IconWatchlist from 'assets/customIcon/PopoverNavbarContainer/IconWatchlist';
import IconChartbit from 'assets/customIcon/PopoverNavbarContainer/IconChartbit';
import IconOrderbook from 'assets/customIcon/PopoverNavbarContainer/IconOrderbook';
import IconEIpo from 'assets/customIcon/PopoverNavbarContainer/IconEIpo';
import IconAcademy from 'assets/customIcon/PopoverNavbarContainer/IconAcademy';
import IconScreener from 'assets/customIcon/PopoverNavbarContainer/IconScreener';
import IconValuation from 'assets/customIcon/PopoverNavbarContainer/IconValuation';
import IconFinancials from 'assets/customIcon/PopoverNavbarContainer/IconFinancials';
import IconFundachart from 'assets/customIcon/PopoverNavbarContainer/IconFundachart';
import IconSeasonality from 'assets/customIcon/PopoverNavbarContainer/IconSeasonality';
import IconSector from 'assets/customIcon/PopoverNavbarContainer/IconSector';
import IconCalender from 'assets/customIcon/PopoverNavbarContainer/IconCalender';
import IconEarnings from 'assets/customIcon/PopoverNavbarContainer/IconEarnings';
import IconGlossary from 'assets/customIcon/PopoverNavbarContainer/IconGlossary';
import IconEBook from 'assets/customIcon/PopoverNavbarContainer/IconEBook';
import IconPortfolio from 'assets/customIcon/PopoverNavbarContainer/IconPortfolio';
import useAuthModalStore from 'features/securities/AuthModal/store';
import IconInsider from 'assets/customIcon/PopoverNavbarContainer/IconInsider';
import useTradingAccount from 'hooks/useTradingAccount';
import ReloadHandler from './ReloadHandler';
import { NavbarWeb, Navigation } from './styled';

const NavMoreLogo = () => (
  <Flex width="40px" height="40px" align="center" justify="center">
    <Image src="/icon/navbar-web/tripel-dot.svg" alt="Tripel Dot" />
  </Flex>
);

const Web = ({ minimize, ...otherProps }) => {
  const router = useRouter();
  const { data } = useSelector(selectors);
  const { country = 'ID' } = data;
  const { tradingType } = useTradingAccount();

  const navbarRef = useRef(null);
  const [showNavMoreLogo, setShowNavMoreLogo] = useState(true);
  const showAuthModal = useAuthModalStore((state) => state.showModal);
  const tradingModalConfig = useAuthModalStore(
    (state) => state.tradingModalConfig,
  );
  const closeAuthModal = useAuthModalStore((state) => state.closeModal);

  const updateNavMoreLogoVisibility = () => {
    if (navbarRef.current) {
      const { width } = navbarRef.current.getBoundingClientRect(); // Get the width of the NavbarWeb
      setShowNavMoreLogo(width <= 1873); // Update state based on width
    }
  };

  useEffect(() => {
    updateNavMoreLogoVisibility();

    // Attach resize event listener
    window.addEventListener('resize', updateNavMoreLogoVisibility);

    return () => {
      // Cleanup listener on unmount
      window.removeEventListener('resize', updateNavMoreLogoVisibility);
    };
  }, []);

  const renderExpand = () => {
    if (country === 'ID' && showNavMoreLogo) {
      return (
        <div data-cy={DATA_CY.NAVIGATION.MORE_MENU_BUTTON}>
          <NavMoreLogo />
        </div>
      );
    }

    return null;
  };

  const renderIcon = (logo) => {
    switch (logo) {
      case 'icon-menu3_home':
        return <IconStream />;
      case 'icon-menu3_profile':
        return <IconProfile />;
      case 'icon-menu3_watchlist':
        return <IconWatchlist />;
      case 'icon-menu3_portfolio':
        return <IconPortfolio />;
      case 'icon-menu3_chartbit':
        return <IconChartbit />;
      case 'icon-menu3_orderbook':
        return <IconOrderbook />;
      case 'icon-menu3_eipo':
        return <IconEIpo />;
      case 'icon-menu3_academy':
        return <IconAcademy />;
      case 'icon-menu3_screener':
        return <IconScreener />;
      case 'icon-menu3_valuation':
        return <IconValuation />;
      case 'icon-menu3_financials':
        return <IconFinancials />;
      case 'icon-menu3_fundachart':
        return <IconFundachart />;
      case 'icon-menu3_seasonality':
        return <IconSeasonality />;
      case 'icon-menu3_sector':
        return <IconSector />;
      case 'icon-menu3_calendar':
        return <IconCalender />;
      case 'icon-menu3_earning':
        return <IconEarnings />;
      case 'icon-menu3_glossary':
        return <IconGlossary />;
      case 'icon-menu3_ebook':
        return <IconEBook />;
      case 'icon-insider':
        return <IconInsider />;
      default:
        return null;
    }
  };

  const handleHref = (item) => {
    switch (item) {
      case '/securities/portfolio':
        if (tradingType) {
          return '/securities/portfolio';
        }
        if (router.pathname === '/[username]') {
          return `${data?.username}`;
        }
        return router.asPath;

      default:
        return item;
    }
  };

  // Handle Redirection After Login
  useEffect(() => {
    if (tradingType === 'real' || tradingType === 'virtual') {
      if (tradingModalConfig?.redirectURL === '/securities/portfolio') {
        closeAuthModal();
        router.push(tradingModalConfig?.redirectURL);
      }
    }
  }, [tradingType]);

  return (
    <div ref={navbarRef}>
      <NavbarWeb
        {...otherProps}
        minimize={minimize}
        expandNavLogo={renderExpand}
        isSideWidgetOpen
      >
        {generateNavBar(data).map((item) => (
          <Navigation
            key={item.label}
            active={item.matcher(router)}
            data-cy={item['data-cy']}
          >
            <ReloadHandler
              href={handleHref(item.href)}
              label={item.label}
              onClick={() => {
                try {
                  clevertapEvent(CLEVERTAP_EVENT_NAME.HEADER_MENU, {
                    Action: item.label.toLowerCase(),
                  });
                } catch (e) {
                  console.error(e);
                }
                if (item.label === 'Portfolio') {
                  if (tradingType) {
                    router.push(item.href);
                  } else {
                    showAuthModal({
                      redirectURL: '/securities/portfolio',
                      isRedirect: true,
                    });
                  }
                }
              }}
              isMobile={false}
            >
              {renderIcon(item.logoMobile)}
              <Text as="a" color="inherit" pl="6px">
                {item.label}
              </Text>
            </ReloadHandler>
          </Navigation>
        ))}
      </NavbarWeb>
    </div>
  );
};

Web.propTypes = {
  minimize: PropTypes.bool,
};

Web.defaultProps = {
  minimize: false,
};

export default Web;
